import classNames from "../../utils/classNames";
import EventEmitter from "./../EventEmitter";
import {filter} from "lodash";
import {resolveContainer} from "./domUtils";
import IntegrationError from "../IntegrationError";
import Controller from "../controller/Controller";
import ComponentFrame from "./ComponentFrame";
import {COMPANY_NAME} from "../../env";
import { ComponentType } from "../types";

const VOID_ELEMENTS = ['AREA', 'BASE', 'BR', 'COL', 'COMMAND', 'EMBED', 'HR', 'IMG', 'INPUT', 'KEYGEN', 'LINK', 'META', 'PARAM', 'SOURCE', 'TRACK', 'WBR'];

const CLEARABLE_STYLES = {
    margin: 0,
    padding: 0,
    border: "none",
    display: "block",
    background: "transparent",
    position: "relative",
    opacity: 1,
};

export default class Component extends EventEmitter {

    _empty: boolean;
    _focus: boolean;
    _invalid: boolean;
    _frame: ComponentFrame;
    _component: HTMLDivElement;
    _container: HTMLElement;
    _controllerId: string;

    constructor(controller, componentName, options?) {
        super();

        this._setupComponent(controller, componentName, options);
        this._controllerId = controller._id;
        this._empty = true;
        this._focus = false;
        this._invalid = false;
    }

    updateOptions(options) {
        this._frame.send({
          action: 'securionpay-frame-update-options',
          payload: options,
        });
      }

    _formSubmit() {
        let formParent = this._component.parentElement;
        while (formParent && "FORM" !== formParent.nodeName) {
            formParent = formParent.parentElement;
        }

        if (formParent) {
            let event = document.createEvent("Event");
            event.initEvent("submit", true, true);
            formParent.dispatchEvent(event);
        }
    }

    _setupComponent(controller: Controller, name: ComponentType, options) {
        let component = document.createElement("div");
        component.className = `__private${COMPANY_NAME}Component`;

        component.style.cssText = Object.keys(CLEARABLE_STYLES).map(function (style) {
            return style + ": " + CLEARABLE_STYLES[style] + " !important;"
        }).join(" ");

        let frame = controller.createComponentFrame(name, options);

        this._component = component;
        this._frame = frame;
        this._frame.on("submit", this._formSubmit.bind(this));
        this._frame.on("focus", () => {
            this._focus = true;
            this.emit("focus");
            this._updateClasses();
        });
        this._frame.on("blur", () => {
            this._focus = false;
            this.emit("blur");
            this._updateClasses();
        });
        this._frame.on("change", (data) => {
            this._empty = data.empty;
            this._invalid = data.error;
            this.emit("change", data);
            this._updateClasses();
        });

        this._frame.on("redirect-focus", (data) => {
            let elements = document.querySelectorAll("a[href], area[href], input:not([disabled])," +
                "select:not([disabled]), textarea:not([disabled]), " +
                "button:not([disabled]), *[tabindex], *[contenteditable]");

            let focusableElements = Array.prototype.filter.call(elements, (e) => {
                let tabindex = e.getAttribute("tabindex");
                let isVisible = e.getBoundingClientRect().width > 0 && e.getBoundingClientRect().height > 0;
                return (!tabindex || parseInt(tabindex) !== -1) && isVisible;
            });

            let indexOf = focusableElements.findIndex((e) => {
                return e === this._component || this._component.contains(e);
            });
            let nextIndex = indexOf + ("previous" === data.direction ? -1 : 1);
            let result = focusableElements[nextIndex];

            // @ts-ignore
            document.activeElement && document.activeElement.blur && document.activeElement.blur();
            result.focus();
        });

        this._frame.on('load', () => {
            this.emit("ready");
        });
    }

    focus() {
        this._frame.focus();
    }

    mount(selector) {
        let container = resolveContainer(selector);
        if (VOID_ELEMENTS.indexOf(container.nodeName) !== -1) {
            throw new IntegrationError("Container should be a DOM element which can have children.");
        }

        if (this._frame._isMounted()) {
            throw new IntegrationError("Component is already mounted.");
        }

        if (container.children.length) {
            console.warn("Component will be mounted to DOM element with children.");
        }

        this._frame.appendTo(this._component);

        let input = document.createElement("input");
        input.className = `__private${COMPANY_NAME}Input`;
        let styles = {
            border: "none",
            display: "block",
            position: "absolute",
            height: "1px",
            top: 0,
            left: 0,
            padding: 0,
            margin: 0,
            width: "100%",
            opacity: 0,
            background: "transparent",
            pointerEvents: "none"
        };
        input.style.cssText = Object.keys(styles).map(function (style) {
            return style + ": " + styles[style] + " !important;"
        }).join(" ");
        input.setAttribute("aria-hidden", "true");

        input.addEventListener("focus", () => {
            this._frame.focus()
        });

        container.appendChild(this._component);
        this._component.appendChild(input);

        this._container = container;

        this._handleInputFocusAfterCorrespondingLabelClick();
        this._updateClasses();

        return this;
    }

    clear() {
        this._frame.send({action: 'securionpay-component-clear'});

        return this;
    }

    _updateClasses() {
        if (this._container) {
            let otherClassNames = filter(this._container.classList, (cl) => cl.indexOf(`${COMPANY_NAME}Component`) === -1).join(" ");
            this._container.className = classNames({
                [`${otherClassNames}`]: true,
                [`${COMPANY_NAME}Component`]: true,
                [`${COMPANY_NAME}Component--empty`]: this._empty,
                [`${COMPANY_NAME}Component--focus`]: this._focus,
                [`${COMPANY_NAME}Component--invalid`]: this._invalid,
            });
        }
    }

    _handleInputFocusAfterCorrespondingLabelClick() {
        const containerId = this._container.getAttribute("id");
        if (containerId) {
            const labels = document.querySelectorAll(`label[for='${containerId}']`);
            for (let i = 0; i < labels.length; i++) {
                labels[i].addEventListener('click', () => {
                    this._frame.focus();
                });
            }
        }
    }
}
